import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useJobLog } from "@/hooks/use-job-log";
import { useHaptics } from "@/hooks/use-haptics";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { wireHeaders, wireRows, conduitHeaders, conduitRows, ELECTRICAL_CATEGORIES as BASE_CATEGORIES } from "@/components/electrical/electrical-helpers";
import { GroundLookup, MotorFLA, WireSizing, VoltageDropTable } from "@/components/electrical/ConductorTools";
import { ConduitFillCalc, BoxFillCalc, MultiRacewayFill, ConduitBendingTool } from "@/components/electrical/RacewayTools";
import { MotorStarterSizing, DisconnectSizing, MotorFeederSizing, VFDSizingTool } from "@/components/electrical/MotorTools";
import { TransformerSizing, PanelScheduleCalc, FaultCurrentCalc, PowerFactorCorrection, DemandFactorCalc, ServiceEntranceSizing } from "@/components/electrical/EquipmentTools";
import { VoltageDropCalc, LightingLoadCalc, bendRadiusHeaders, bendRadiusRows } from "@/components/electrical/ElectricalExtras";
import { useSwipeTabs } from "@/hooks/use-swipe-tabs";
import SwipeAnimatedContent from "@/components/SwipeAnimatedContent";
import TabDots from "@/components/TabDots";
import TradeFormulas from "@/components/TradeFormulas";

// Add formulas to the equipment category
const ELECTRICAL_CATEGORIES = BASE_CATEGORIES.map(cat =>
  cat.id === "equipment"
    ? { ...cat, tools: [...cat.tools, { value: "formulas", label: "Formulas" }] }
    : cat
);

const Electrical = () => {
  const { addEntry } = useJobLog();
  const { lightTap } = useHaptics();
  const [searchParams] = useSearchParams();

  const requestedTool = searchParams.get("tool");
  const findCategoryForTool = (toolValue: string) =>
    ELECTRICAL_CATEGORIES.find(c => c.tools.some(t => t.value === toolValue));
  const initialCat = requestedTool ? findCategoryForTool(requestedTool) : null;

  const [category, setCategory] = useState(initialCat?.id ?? "conductors");
  const [activeTool, setActiveTool] = useState(requestedTool ?? "wire");
  const [sharedDemandVA, setSharedDemandVA] = useState<number | null>(null);
  const [cameFromTool, setCameFromTool] = useState<string | null>(null);
  const currentCategory = ELECTRICAL_CATEGORIES.find(c => c.id === category)!;

  useEffect(() => {
    if (requestedTool) {
      const cat = findCategoryForTool(requestedTool);
      if (cat) {
        setCategory(cat.id);
        setActiveTool(requestedTool);
      }
    }
  }, [requestedTool]);

  const handleCategoryChange = (catId: string) => {
    lightTap();
    setCategory(catId);
    const cat = ELECTRICAL_CATEGORIES.find(c => c.id === catId)!;
    setActiveTool(cat.tools[0].value);
  };

  const handleToolChange = useCallback((tool: string) => {
    lightTap();
    setActiveTool(tool);
  }, [lightTap]);

  const toolValues = currentCategory.tools.map(t => t.value);
  const handleBoundarySwipe = useCallback((dir: "left" | "right") => {
    const catIdx = ELECTRICAL_CATEGORIES.findIndex(c => c.id === category);
    if (dir === "left" && catIdx < ELECTRICAL_CATEGORIES.length - 1) {
      const next = ELECTRICAL_CATEGORIES[catIdx + 1];
      lightTap(); setCategory(next.id); setActiveTool(next.tools[0].value);
      toast(next.label, { duration: 1200 });
    } else if (dir === "right" && catIdx > 0) {
      const prev = ELECTRICAL_CATEGORIES[catIdx - 1];
      lightTap(); setCategory(prev.id); setActiveTool(prev.tools[prev.tools.length - 1].value);
      toast(prev.label, { duration: 1200 });
    }
  }, [category, lightTap]);
  const swipeHandlers = useSwipeTabs({ tabs: toolValues, activeTab: activeTool, onChangeTab: handleToolChange, onBoundarySwipe: handleBoundarySwipe });

  const renderTool = () => {
    switch (activeTool) {
      case "wire": return <div className="-mx-4"><DataTable headers={wireHeaders} rows={wireRows} searchable searchPlaceholder="Search wire sizes…" favoritesConfig={{ type: "wire", category: "⚡ Wire Ampacity", makeId: (row) => `wire-${row[0]}`, makeName: (row) => `${row[0]} AWG`, makeDetail: (row) => `Cu ${row[3]}A · Al ${row[4]}A · ${row[2]} kcmil` }} /></div>;
      case "conduit": return <div className="-mx-4"><DataTable headers={conduitHeaders} rows={conduitRows} searchable searchPlaceholder="Search conduit sizes…" favoritesConfig={{ type: "wire", category: "⚡ Conduit", makeId: (row) => `conduit-${row[0]}`, makeName: (row) => `Conduit ${row[0]}`, makeDetail: (row) => `ID ${row[1]}" · Area ${row[2]} in² · 40% Fill ${row[3]} in²` }} /></div>;
      case "ground": return <GroundLookup onSave={addEntry} />;
      case "motor": return <MotorFLA />;
      case "sizing": return <WireSizing onSave={addEntry} />;
      case "fill": return <ConduitFillCalc onSave={addEntry} />;
      case "boxfill": return <BoxFillCalc onSave={addEntry} />;
      case "xfmr": return <TransformerSizing onSave={addEntry} />;
      case "vdtable": return <VoltageDropTable />;
      case "raceway": return <MultiRacewayFill onSave={addEntry} />;
      case "panel": return <PanelScheduleCalc onSendToService={(va) => { setSharedDemandVA(va); setCameFromTool("panel"); setCategory("equipment"); setActiveTool("service"); }} onSave={addEntry} />;
      case "fault": return <FaultCurrentCalc onSave={addEntry} />;
      case "bending": return <ConduitBendingTool onSave={addEntry} />;
      case "starter": return <MotorStarterSizing onSave={addEntry} />;
      case "disco": return <DisconnectSizing onSave={addEntry} />;
      case "feeder": return <MotorFeederSizing onSave={addEntry} />;
      case "vfd": return <VFDSizingTool onSave={addEntry} />;
      case "pfc": return <PowerFactorCorrection onSave={addEntry} />;
      case "demand": return <DemandFactorCalc onSave={addEntry} onSendToService={(va) => { setSharedDemandVA(va); setCameFromTool("demand"); setCategory("equipment"); setActiveTool("service"); }} />;
      case "service": return <ServiceEntranceSizing onSave={addEntry} initialDemandVA={sharedDemandVA} onConsumedInitial={() => setSharedDemandVA(null)} cameFrom={cameFromTool} onGoBack={(tool) => { setCameFromTool(null); setActiveTool(tool); }} />;
      case "vdcalc": return <VoltageDropCalc />;
      case "bendradius": return <div className="-mx-4"><DataTable headers={bendRadiusHeaders} rows={bendRadiusRows} searchable searchPlaceholder="Search bend radius…" favoritesConfig={{ type: "wire", category: "⚡ Bend Radius", makeId: (row) => `bendrad-${row[0]}`, makeName: (row) => `Bend ${row[0]}`, makeDetail: (row) => `EMT ${row[1]}" · IMC ${row[2]}" · RMC ${row[3]}"` }} /></div>;
      case "lighting": return <LightingLoadCalc />;
      case "formulas": return <TradeFormulas trade="electrical" />;
      default: return null;
    }
  };

  return (
    <div className="pb-24 animate-fade-in">
      <PageHeader title="⚡ Electrical Reference" subtitle="Wire, conduit, grounding, motor, sizing, fill & more" />
      <div className="px-4 mt-2">
        <div className="flex gap-1.5 bg-secondary rounded-lg p-1">
          {ELECTRICAL_CATEGORIES.map(cat => (
            <button key={cat.id} onClick={() => handleCategoryChange(cat.id)}
              className={`flex-1 py-2 px-2 rounded-md text-xs font-semibold transition-all ${category === cat.id ? `${cat.color} text-primary-foreground shadow-sm` : "text-muted-foreground hover:text-foreground"}`}>
              {cat.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 mt-2 bg-secondary/50 rounded-lg p-1 overflow-x-auto no-scrollbar">
          {currentCategory.tools.map(tool => (
            <button key={tool.value} onClick={() => swipeHandlers.changeTab(tool.value)}
              className={`shrink-0 py-1.5 px-3 rounded-md text-xs font-medium transition-all whitespace-nowrap ${activeTool === tool.value ? "bg-card text-foreground shadow-sm border border-border" : "text-muted-foreground hover:text-foreground"}`}>
              {tool.label}
            </button>
          ))}
        </div>
      </div>
      <div className="px-4 mt-3" {...{onTouchStart: swipeHandlers.onTouchStart, onTouchMove: swipeHandlers.onTouchMove, onTouchEnd: swipeHandlers.onTouchEnd}}>
        <SwipeAnimatedContent activeKey={activeTool} swipeDirection={swipeHandlers.swipeDirection} edgeBounce={swipeHandlers.edgeBounce} onAnimationDone={swipeHandlers.clearDirection} onEdgeBounceDone={swipeHandlers.clearEdgeBounce}>
          {renderTool()}
        </SwipeAnimatedContent>
        <TabDots total={toolValues.length} activeIndex={toolValues.indexOf(activeTool)} onDotClick={(i) => swipeHandlers.changeTab(toolValues[i])} />
      </div>
    </div>
  );
};

export default Electrical;
